import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';

import { Row, Col } from 'antd';
import { Field, FormikProps } from 'formik';

// REDUX
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import data from 'store/modules/enum';
import dataCrv from 'store/modules/api/detranCrv/enum';
import {
	consultarMunicipioCodigoProdesClear,
	consultarMunicipioCodigoProdespRequest,
} from 'store/modules/api/detranCrv/enum/consultarMunicipioCodigoProdesp/actions';

// COMPONENTS
import Section from 'components/Common/Section';
import InputMask from 'components/Common/Form/Input/InputMask';
import Input from 'components/Common/Form/Input/Input';
import Select from 'components/Common/Form/Select';
import Placa from 'components/Common/Form/Fields/Placa';
import CEP, { IBuscarCepResultado } from 'components/Common/Form/Fields/CEP';
import CpfCnpj from 'components/Common/Form/Fields/CpfCnpj';

// UTILS
import hasError from 'utils/getFormErrors';
import {
	onlyNumbers,
	onlyLetters,
	onlyNumbersLettersSpace,
} from 'utils/genericFunctions';

// FORM
import { IEmiteTransferenciaVeiculo, initialValues } from '../form';

interface Props {
	formik: FormikProps<IEmiteTransferenciaVeiculo>;
	isOpenDocument: boolean;
}

const DadosProprietario: React.FC<Props> = ({
	formik,
	isOpenDocument = false,
}) => {
	const dispatch = useDispatch();

	const { consultarMunicipioPorUF, consultarMunicipioCodigoProdesp } =
		useSelector((state: ApplicationState) => state.api.detranCrv.Enum);

	const { consultarVeiculoV110, consultarVeiculoV120 } = useSelector(
		(state: ApplicationState) => state.api.detranCrv.solicitarDocumentos,
	);

	const [tipoPessoa, setTipoPessoa] = useState<string>('');

	const handleChangeIdentification = (value: string) => {
		setTipoPessoa(value);
		formik.setFieldValue('identificacao', value);

		if (value === 'JURIDICA') {
			formik.setFieldValue('rgProprietario', '');
			formik.setFieldValue('orgaoExpedidorProprietario', '');
			formik.setFieldValue('ufProprietario', '');
		}
	};

	useEffect(() => {
		formik.setFieldValue('servicoDigital', isOpenDocument);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (
			consultarMunicipioCodigoProdesp.data &&
			consultarMunicipioCodigoProdesp.status === 200
		) {
			formik.setFieldValue(
				'codigoMunicipioProprietario',
				Number(consultarMunicipioCodigoProdesp.data.codigoPRODESP),
			);
			dispatch(consultarMunicipioCodigoProdesClear());
		}
	}, [
		consultarMunicipioCodigoProdesp.data,
		consultarMunicipioCodigoProdesp.status,
		dispatch,
		formik,
	]);

	useEffect(() => {
		if (consultarVeiculoV110.data) {
			if (
				consultarVeiculoV110.data?.caracteristicaVeiculo?.indicadorRenave ===
					1 ||
				consultarVeiculoV110.data?.caracteristicaVeiculo?.indicadorRenave === 2
			) {
				const { tipoDocumentoLojaComprador } =
					consultarVeiculoV110.data.caracteristicaVeiculo;

				setTipoPessoa(tipoDocumentoLojaComprador === 1 ? 'FISICA' : 'JURIDICA');
			} else {
				const { proprietario } =
					consultarVeiculoV110.data.caracteristicaVeiculo;

				setTipoPessoa(proprietario.identificacao);
			}
		}
	}, [consultarVeiculoV110.data]);

	const clearFormikAddressFields = useCallback(() => {
		formik.setFieldValue('complementoProprietario', '');
		formik.setFieldValue('numeroProprietario', '');
	}, [formik]);

	const handleCepSelecionado = useCallback(
		(res: IBuscarCepResultado) => {
			clearFormikAddressFields();

			if (res.codigoMunicipio) {
				const consultaMunicipioData = {
					evento: null,
					codigoMunicipioCorreio: res.codigoMunicipio.toString(),
				};
				dispatch(consultarMunicipioCodigoProdespRequest(consultaMunicipioData));
			}
			formik.setFieldValue('cepProprietario', res.cep);
			formik.setFieldValue('bairroProprietario', res.bairro.substring(0, 70));
			formik.setFieldValue(
				'enderecoProprietario',
				res.endereco.substring(0, 70),
			);
			formik.setFieldValue('descricaoMunicipioProprietario', res.municipio);
		},
		[clearFormikAddressFields, dispatch, formik],
	);

	return (
		<>
			<Row gutter={[0, 10]}>
				<Col span={24}>
					<Section size="lg" title="Dados do Proprietário" titleSize="sm">
						<Row gutter={[0, 10]}>
							<Col span={24}>
								<Field
									as={Input}
									title="Nome do Proprietário"
									titleSize="xl"
									name="nomeProprietario"
									maxLength={60}
									size={87}
									required
									error={hasError(formik.errors, 'nomeProprietario')}
									onChange={(event: ChangeEvent<HTMLInputElement>) => {
										formik.setFieldValue(
											'nomeProprietario',
											onlyNumbersLettersSpace(
												event.target.value,
											).toLocaleUpperCase(),
										);
									}}
								/>
							</Col>
						</Row>

						<Row gutter={[0, 10]}>
							<Col span={11}>
								<Field
									as={CEP}
									title="CEP do Imóvel do Proprietário"
									titleSize="xl"
									name="cepProprietario"
									defaultValue={formik.values.cepProprietario}
									required
									size={80}
									formik={formik}
									result={(res: IBuscarCepResultado) => {
										handleCepSelecionado(res);
									}}
									error={hasError(formik.errors, 'cepProprietario')}
								/>
							</Col>

							<Col span={11}>
								<Field
									as={Input}
									title="Bairro"
									titleSize="xl"
									name="bairroProprietario"
									maxLength={70}
									size={92}
									required
									error={hasError(formik.errors, 'bairroProprietario')}
								/>
							</Col>
						</Row>

						<Row gutter={[0, 10]}>
							<Col span={11}>
								<Field
									as={Input}
									title="Endereço do Proprietário"
									titleSize="xl"
									name="enderecoProprietario"
									maxLength={70}
									size={80}
									required
									error={hasError(formik.errors, 'enderecoProprietario')}
								/>
							</Col>

							<Col span={11}>
								<Field
									as={InputMask}
									title="Número"
									titleSize="xl"
									name="numeroProprietario"
									mask="99999"
									maskChar=""
									alwaysShowMask={false}
									size={92}
									required
									error={hasError(formik.errors, 'numeroProprietario')}
									onChange={(e: { target: { value: string } }) => {
										formik.setFieldValue(
											'numeroProprietario',
											onlyNumbers(e.target.value),
										);
									}}
								/>
							</Col>
						</Row>
						<Row gutter={[0, 10]}>
							<Col span={11}>
								<Field
									as={Input}
									title="Complemento"
									titleSize="xl"
									name="complementoProprietario"
									maxLength={35}
									size={80}
									onChange={(e: { target: { value: string } }) => {
										formik.setFieldValue(
											'complementoProprietario',
											onlyNumbersLettersSpace(
												e.target.value.toLocaleUpperCase(),
											),
										);
									}}
									error={!!formik.errors.complementoProprietario}
								/>
							</Col>

							<Col span={11}>
								<Field
									as={InputMask}
									title="Telefone"
									titleSize="xl"
									name="telefone"
									mask="(99)9999-9999"
									size={92}
									onChange={(e: { target: { value: string } }) => {
										formik.setFieldValue(
											'telefone',
											onlyNumbers(e.target.value.toLocaleUpperCase()),
										);
									}}
									error={!!formik.errors.telefone}
								/>
							</Col>
						</Row>

						<Row gutter={[0, 10]}>
							<Col span={11}>
								<Field
									as={Select}
									title="Município do Proprietário"
									titleSize="xl"
									name="codigoMunicipioProprietario"
									options={consultarMunicipioPorUF.data}
									onChange={(v: number) => {
										formik.setFieldValue('codigoMunicipioProprietario', v);

										const selectedItem = consultarMunicipioPorUF.data.filter(
											item => v === item.value,
										);

										formik.setFieldValue(
											'descricaoMunicipioProprietario',
											selectedItem[0].label,
										);
									}}
									size={80}
									required
									error={hasError(formik.errors, 'codigoMunicipioProprietario')}
								/>
							</Col>

							<Col span={11}>
								<Field
									as={Select}
									title="Identificação do Contribuinte"
									titleSize="xl"
									name="identificacao"
									defaultFirstOption={false}
									options={dataCrv.identificacao}
									onChange={(v: string) => {
										handleChangeIdentification(v);
									}}
									size={92}
									required
									error={hasError(formik.errors, 'validacaoIdentificacao')}
								/>
							</Col>
						</Row>

						<Row gutter={[0, 10]}>
							<Col span={11}>
								<Field
									as={CpfCnpj}
									title="CPF/CNPJ Proprietário"
									titleSize="xl"
									name="cpfCnpjProprietario"
									defaultValue={initialValues.cpfCnpjProprietario}
									maskType={
										formik.values.identificacao === 'FISICA' ? 'cpf' : 'cnpj'
									}
									size={80}
									required
									formik={formik}
									error={hasError(formik.errors, 'cpfCnpjProprietario')}
								/>
							</Col>

							<Col
								span={11}
								style={{
									marginLeft: '40px',
								}}
							>
								<Row>
									<Col span={21}>
										<Field
											as={InputMask}
											title="RG"
											titleSize="lg"
											name="rgProprietario"
											mask="99999999999"
											required={formik.values.identificacao === 'FISICA'}
											error={hasError(formik.errors, 'validacaoRgProprietario')}
										/>
									</Col>
								</Row>
							</Col>
						</Row>

						<>
							<Row gutter={[0, 10]}>
								<Col span={11}>
									<Field
										as={Input}
										title="Órgão Expedidor"
										titleSize="xl"
										name="orgaoExpedidorProprietario"
										maxLength={5}
										size={80}
										required={formik.values.identificacao === 'FISICA'}
										error={hasError(
											formik.errors,
											'validacaoOrgaoExpedidorProprietario',
										)}
										onChange={(e: { target: { value: string } }) => {
											formik.setFieldValue(
												'orgaoExpedidorProprietario',
												onlyLetters(e.target.value.toLocaleUpperCase()),
											);
										}}
									/>
								</Col>

								<Col span={11}>
									<Field
										as={Select}
										title="UF"
										titleSize="xl"
										name="ufProprietario"
										defaultFirstOption={false}
										options={data.uf}
										onChange={(value: string) =>
											formik.setFieldValue('ufProprietario', value)
										}
										size={92}
										required={formik.values.identificacao === 'FISICA'}
										error={hasError(formik.errors, 'validacaoUfRgProprietario')}
									/>
								</Col>
							</Row>
						</>

						{isOpenDocument && (
							<Row gutter={[0, 10]}>
								<Col span={11}>
									<Field
										as={Input}
										title="E-mail"
										titleSize="xl"
										name="emailCidadao"
										maxLength={60}
										size={80}
										required={formik.values.servicoDigital}
										error={hasError(formik.errors, 'validacaoEmailCidadao')}
									/>
								</Col>

								<Col span={11}>
									<Field
										as={InputMask}
										title="Telefone de Contato"
										titleSize="xl"
										name="dddTelefoneCidadao"
										size={92}
										mask="(99)9999-99999"
										required={formik.values.servicoDigital}
										error={!!formik.errors.dddTelefoneCidadao}
									/>
								</Col>
							</Row>
						)}

						<Row gutter={tipoPessoa === 'FISICA' ? [0, 10] : undefined}>
							<Col span={11}>
								<Field
									as={Input}
									title="Nome do Proprietário Anterior"
									titleSize="xl"
									name="nomeProprietarioAnterior"
									maxLength={60}
									size={80}
									required
									error={hasError(
										formik.errors,
										'validacaoNomeProprietarioAnterior',
									)}
									onChange={(event: ChangeEvent<HTMLInputElement>) => {
										formik.setFieldValue(
											'nomeProprietarioAnterior',
											onlyNumbersLettersSpace(
												event.target.value,
											).toLocaleUpperCase(),
										);
									}}
								/>
							</Col>

							<Col span={11}>
								<Field
									as={Placa}
									title="Placa Anterior"
									titleSize="xl"
									name="placaAnterior"
									defaultValue={initialValues.placaAnterior}
									size={92}
									required
									formik={formik}
									error={hasError(formik.errors, 'validacaoPlacaAnterior')}
								/>
							</Col>
						</Row>

						<Row gutter={tipoPessoa === 'FISICA' ? [0, 10] : undefined}>
							<Col span={11}>
								<Field
									as={Select}
									title="Município Placa Anterior"
									titleSize="xl"
									name="codigoMunicipioPlacaAnterior"
									options={
										consultarVeiculoV120.data
											? consultarMunicipioPorUF.dataOutrosEstados
											: consultarMunicipioPorUF.data
									}
									onChange={(v: number) => {
										formik.setFieldValue('codigoMunicipioPlacaAnterior', v);

										const selectedItem = consultarMunicipioPorUF.data.filter(
											item => v === item.value,
										);

										formik.setFieldValue(
											'descricaoMunicipioPlacaAnterior',
											selectedItem[0].label,
										);
									}}
									size={80}
									required
									disabled
									error={hasError(
										formik.errors,
										'validacaoMunicipioPlacaAnterior',
									)}
								/>
							</Col>

							<Col span={11}>
								<Field
									as={Select}
									title="UF da Placa Anterior"
									titleSize="xl"
									name="ufPlacaAnterior"
									defaultFirstOption={false}
									options={data.uf}
									onChange={(value: string) =>
										formik.setFieldValue('ufPlacaAnterior', value)
									}
									size={92}
									disabled
									required
									error={hasError(formik.errors, 'validacaoUfPlacaAnterior')}
								/>
							</Col>
						</Row>
					</Section>
				</Col>
			</Row>
		</>
	);
};

export default DadosProprietario;
